import React, { createContext, useContext, useState, ReactNode } from 'react';
import { X, AlertCircle, HelpCircle, Info } from 'lucide-react';

interface DialogOptions {
    title?: string;
    message: string;
    type: 'alert' | 'confirm' | 'prompt';
    defaultValue?: string;
}

interface DialogContextType {
    showAlert: (message: string, title?: string) => Promise<void>;
    showConfirm: (message: string, title?: string) => Promise<boolean>;
    showPrompt: (message: string, defaultValue?: string, title?: string) => Promise<string | null>;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const useDialog = () => {
    const context = useContext(DialogContext);
    if (!context) {
        throw new Error('useDialog must be used within a DialogProvider');
    }
    return context;
};

export const DialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [dialogState, setDialogState] = useState<{
        isOpen: boolean;
        options: DialogOptions | null;
        resolve: (value: any) => void;
    }>({
        isOpen: false,
        options: null,
        resolve: () => {},
    });

    const [promptValue, setPromptValue] = useState('');

    const openDialog = (options: DialogOptions): Promise<any> => {
        setPromptValue(options.defaultValue || '');
        return new Promise((resolve) => {
            setDialogState({
                isOpen: true,
                options,
                resolve,
            });
        });
    };

    const showAlert = (message: string, title?: string) => {
        return openDialog({ message, title: title || 'Avviso', type: 'alert' });
    };

    const showConfirm = (message: string, title?: string) => {
        return openDialog({ message, title: title || 'Conferma', type: 'confirm' });
    };

    const showPrompt = (message: string, defaultValue?: string, title?: string) => {
        return openDialog({ message, defaultValue, title: title || 'Inserisci', type: 'prompt' });
    };

    const handleClose = (value: any) => {
        setDialogState((prev) => {
            prev.resolve(value);
            return {
                isOpen: false,
                options: null,
                resolve: () => {},
            };
        });
    };

    return (
        <DialogContext.Provider value={{ showAlert, showConfirm, showPrompt }}>
            {children}
            {dialogState.isOpen && dialogState.options && (
                <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        {/* Header */}
                        <div className="bg-indigo-600 p-4 flex items-center justify-between text-white">
                            <div className="flex items-center gap-2 font-bold">
                                {dialogState.options.type === 'alert' && <AlertCircle size={20} />}
                                {dialogState.options.type === 'confirm' && <HelpCircle size={20} />}
                                {dialogState.options.type === 'prompt' && <Info size={20} />}
                                <span>{dialogState.options.title}</span>
                            </div>
                            {dialogState.options.type !== 'alert' && (
                                <button onClick={() => handleClose(dialogState.options?.type === 'prompt' ? null : false)} className="text-white hover:text-gray-200 transition">
                                    <X size={20} />
                                </button>
                            )}
                        </div>

                        {/* Body */}
                        <div className="p-6">
                            <p className="text-gray-700 whitespace-pre-wrap">{dialogState.options.message}</p>
                            
                            {dialogState.options.type === 'prompt' && (
                                <div className="mt-4">
                                    <input 
                                        type="text" 
                                        autoFocus
                                        value={promptValue} 
                                        onChange={(e) => setPromptValue(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleClose(promptValue);
                                        }}
                                        className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50">
                            {(dialogState.options.type === 'confirm' || dialogState.options.type === 'prompt') && (
                                <button 
                                    onClick={() => handleClose(dialogState.options?.type === 'prompt' ? null : false)}
                                    className="px-5 py-2.5 rounded-xl font-bold text-gray-600 bg-white border border-gray-300 hover:bg-gray-100 transition-colors"
                                >
                                    Annulla
                                </button>
                            )}
                            <button 
                                onClick={() => {
                                    if (dialogState.options?.type === 'alert') handleClose(undefined);
                                    else if (dialogState.options?.type === 'confirm') handleClose(true);
                                    else handleClose(promptValue);
                                }}
                                className="px-5 py-2.5 rounded-xl font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
                            >
                                {dialogState.options.type === 'alert' ? 'OK' : 'Conferma'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </DialogContext.Provider>
    );
};
